import { deleteDB, openDB } from 'idb'
import { GUEST_SCOPE } from '../auth/planLimits'
import { APP_DATABASE_NAME } from '../utils/storageNames'
import { workspaceDatabaseName, workspaceLockName, type WorkspaceScope } from './workspace'

/**
 * Deleting what a person without an account has saved on this device (notes, images and drafts).
 *
 * The app holds its database open while it runs, so the request is only recorded and the page reloads; the deletion is
 * finished at the next start, before anything opens the workspace (`WorkspaceBoundary`). An interrupted deletion is
 * repeated at the next start. Accounts are not touched: an account's offline copy is removed through sign-out, and the
 * account itself through "Delete account".
 *
 * Not covered: content that Android's native capture inbox still holds for a guest (docs/ARCHITECTURE.md#deferred-data-checks).
 */
const REQUEST_KEY = 'minimany:guest-data-removal:v1'
// Browser databases the retired Contexts feature left on devices; removing a guest's data still removes them.
const RETIRED_CONTEXTS_DATABASE = 'minimany-contexts-v2'
const retiredContextsOwnerDatabase = (accountId: string) => `${RETIRED_CONTEXTS_DATABASE}-owner:${accountId}`

export function requestGuestDataRemoval(scope: WorkspaceScope): void {
  if (!navigator.locks) throw new Error('This browser cannot safely coordinate removal across tabs. Close other miniMany tabs and use a browser with Web Locks support.')
  localStorage.setItem(REQUEST_KEY, JSON.stringify({ scope }))
}

export const guestDataRemovalPending = (): boolean => localStorage.getItem(REQUEST_KEY) !== null

async function deleteDatabase(name: string): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    void deleteDB(name, { blocked() { reject(new Error('Removal incomplete. Close other miniMany tabs, then retry.')) } }).then(resolve, reject)
  })
}

/** The first, pre-workspace database belongs to the guest only while no account ever synchronised with it. */
async function legacyDatabaseIsOwnerless(): Promise<boolean> {
  let missing = false
  const db = await openDB(APP_DATABASE_NAME, undefined, { upgrade(_db, version, _next, tx) {
    if (version === 0) { missing = true; void tx.done.catch(() => undefined); tx.abort() }
  } }).catch(error => { if (!missing) throw error; return null })
  if (!db) return false
  try {
    if (!db.objectStoreNames.contains('meta')) return true
    return !(await db.get('meta', 'sync-owner'))?.value
  } finally { db.close() }
}

export async function finishPendingGuestDataRemoval(scope: WorkspaceScope): Promise<void> {
  const raw = localStorage.getItem(REQUEST_KEY)
  if (!raw) return
  const requested = JSON.parse(raw) as { scope?: WorkspaceScope }
  if (requested.scope?.project !== scope.project || requested.scope?.environment !== scope.environment) { localStorage.removeItem(REQUEST_KEY); return }
  if (!navigator.locks) throw new Error('Removal requires Web Locks support.')
  const guestDatabase = workspaceDatabaseName(scope, null)
  await navigator.locks.request(workspaceLockName(guestDatabase), { mode: 'exclusive' }, async () => {
    await deleteDatabase(guestDatabase)
    if (await legacyDatabaseIsOwnerless()) await deleteDatabase(APP_DATABASE_NAME)
    // What the retired Contexts screens derived from those notes, and a guest's own ticks and choices in them. The signed-in
    // owners' statements live in separate per-account databases that this never touches.
    await deleteDatabase(RETIRED_CONTEXTS_DATABASE)
    await deleteDatabase(retiredContextsOwnerDatabase(GUEST_SCOPE))
  })
  // Drafts and other per-workspace values kept outside the database are keyed by its name.
  for (let index = localStorage.length - 1; index >= 0; index--) {
    const key = localStorage.key(index)
    if (key?.startsWith(`${guestDatabase}:`)) localStorage.removeItem(key)
  }
  localStorage.removeItem(REQUEST_KEY)
}
